import { useCallback, useEffect, useRef, useState } from 'react';
import { useMachine } from '@xstate/react';
import { sessionMachine } from '../machines/sessionMachine';
import { playTonePair, getAudioContext } from '../lib/audio';
import { collectDeviceMeta } from '../lib/device';
import { saveSession } from '../lib/db';
import { formatThreshold } from '../lib/color';
import { useLabStore } from '../store/useLabStore';
import type { Modality, PosteriorSummary, SessionResult, TrialRecord, TrialTarget, WorkerOutgoing, WorkerStateMessage } from '../types';
import { createStimulus, Stimulus, type StimulusDefinition } from './Stimulus';

const unitFor = (modality: Modality) => (modality === 'pitch' ? 'cents' : modality === 'contrast' ? 'contrast %' : 'ΔEOK × 100');
const scaleForDisplay = (modality: Modality, value: number) => (modality === 'color' || modality === 'contrast' ? value * 100 : value);

interface Props {
  selected: Modality;
}

export function Experiment({ selected }: Props) {
  const [state, send] = useMachine(sessionMachine);
  const { setLastResult } = useLabStore();
  const [workerState, setWorkerState] = useState<WorkerStateMessage | null>(null);
  const [stimulus, setStimulus] = useState<StimulusDefinition | null>(null);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [isPlayingPitch, setIsPlayingPitch] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const trialsRef = useRef<TrialRecord[]>([]);
  const trialStartRef = useRef<number>(0);
  const latestStimulusRef = useRef<StimulusDefinition | null>(null);

  const cleanupWorker = useCallback(() => {
    workerRef.current?.terminate();
    workerRef.current = null;
  }, []);

  useEffect(() => cleanupWorker, [cleanupWorker]);

  const nextStimulus = useCallback((stateMessage: WorkerStateMessage) => {
    if (stateMessage.done && trialsRef.current.length > 0) return;
    const definition = createStimulus(selected, stateMessage.trialCount + 1, stateMessage.nextDelta);
    setStimulus(definition);
    latestStimulusRef.current = definition;
    trialStartRef.current = performance.now();
    setFeedback(null);
    send({ type: 'STIMULUS_READY' });
    if (definition.modality === 'pitch') {
      setIsPlayingPitch(true);
      playTonePair(definition.pitchBaseHz ?? 440, definition.delta, definition.target as 'first' | 'second')
        .catch((e) => setError(e instanceof Error ? e.message : 'Audio failed.'))
        .finally(() => setIsPlayingPitch(false));
    }
  }, [selected, send]);

  const completeSession = useCallback(async (stateMessage: WorkerStateMessage) => {
    const audioSampleRate = (() => {
      try { return undefined; } catch { return undefined; }
    })();
    const deviceMeta = await collectDeviceMeta(audioSampleRate);
    const result: SessionResult = {
      id: crypto.randomUUID(),
      deviceHash: deviceMeta.deviceHash,
      modality: selected,
      threshold: scaleForDisplay(selected, stateMessage.threshold),
      thresholdLow: scaleForDisplay(selected, stateMessage.low),
      thresholdHigh: scaleForDisplay(selected, stateMessage.high),
      unit: unitFor(selected),
      confidence: stateMessage.confidence,
      trials: trialsRef.current,
      posterior: stateMessage.posterior,
      deviceMeta,
      consentVersion: 'commons-v1',
      createdAt: new Date().toISOString()
    };
    await saveSession(result);
    setLastResult(result);
    send({ type: 'COMPLETE' });
  }, [selected, send, setLastResult]);

  const start = useCallback(async () => {
    cleanupWorker();
    setError(null);
    setFeedback(null);
    trialsRef.current = [];
    setStimulus(null);
    setWorkerState(null);
    setLastResult(null);

    if (selected === 'pitch') {
      const ctx = await getAudioContext();
      await collectDeviceMeta(ctx.sampleRate);
    }

    const worker = new Worker(new URL('../workers/staircaseWorker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;
    worker.addEventListener('message', (event: MessageEvent<WorkerOutgoing>) => {
      const message = event.data;
      if (message.type === 'error') {
        setError(message.error);
        return;
      }
      setWorkerState(message);
      if (message.done && trialsRef.current.length >= 12) {
        void completeSession(message);
      } else {
        nextStimulus(message);
      }
    });
    send({ type: 'START', modality: selected });
    worker.postMessage({ type: 'init', modality: selected });
  }, [cleanupWorker, selected, send, nextStimulus, completeSession, setLastResult]);

  const replayPitch = useCallback(() => {
    const current = latestStimulusRef.current;
    if (!current || current.modality !== 'pitch') return;
    setIsPlayingPitch(true);
    playTonePair(current.pitchBaseHz ?? 440, current.delta, current.target as 'first' | 'second')
      .catch((e) => setError(e instanceof Error ? e.message : 'Audio failed.'))
      .finally(() => setIsPlayingPitch(false));
  }, []);

  const answer = useCallback((answerValue: TrialTarget) => {
    const current = latestStimulusRef.current;
    if (!current || state.matches('feedback') || state.matches('completed')) return;
    const correct = answerValue === current.target;
    const record: TrialRecord = {
      index: current.index,
      modality: current.modality,
      delta: scaleForDisplay(current.modality, current.delta),
      target: current.target,
      answer: answerValue,
      correct,
      rtMs: performance.now() - trialStartRef.current,
      startedAt: new Date().toISOString(),
      base: current.base
    };
    trialsRef.current = [...trialsRef.current, record];
    setFeedback(correct ? 'correct' : 'wrong');
    send({ type: 'ANSWER' });
    window.setTimeout(() => {
      workerRef.current?.postMessage({ type: 'response', delta: current.delta, correct });
      send({ type: 'NEXT', trialIndex: current.index + 1 });
    }, 360);
  }, [send, state]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!stimulus || state.matches('idle') || state.matches('completed')) return;
      if (event.key === 'ArrowLeft') answer('left');
      if (event.key === 'ArrowRight') answer('right');
      if (event.key === '1') answer('first');
      if (event.key === '2') answer('second');
      if (event.key.toLowerCase() === 'r') replayPitch();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [answer, replayPitch, stimulus, state]);

  const running = !state.matches('idle') && !state.matches('completed');
  const progress = workerState ? Math.min(100, Math.round((workerState.trialCount / 30) * 100)) : 0;
  const estimate = workerState ? formatThreshold(selected, scaleForDisplay(selected, workerState.threshold)) : '—';

  return (
    <section className="lab-panel experiment-panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">adaptive threshold run</p>
          <h2>{selected === 'color' ? 'Color Edge' : selected === 'contrast' ? 'Contrast Edge' : 'Pitch Edge'}</h2>
        </div>
        <button className="primary-button" onClick={start}>{running ? 'Restart run' : 'Begin run'}</button>
      </div>

      <div className="meter-stack" aria-label="Run telemetry">
        <div className="meter-row"><span>posterior squeeze</span><span>{workerState ? `${Math.round(workerState.confidence * 100)}%` : '0%'}</span></div>
        <div className="meter"><span style={{ width: `${progress}%` }} /></div>
        <div className="run-stats">
          <span>trials {trialsRef.current.length}</span>
          <span>current edge {estimate}</span>
          <span>{state.value.toString()}</span>
        </div>
      </div>

      {error ? <p className="error-note">{error}</p> : null}

      {state.matches('idle') ? (
        <div className="start-slate">
          <div className="threshold-glyph" aria-hidden="true" />
          <h3>Attention is the instrument.</h3>
          <p>
            You’ll answer a run of forced-choice micro-trials. The browser keeps a Bayesian posterior over your threshold and presses closer to the faint edge after every response.
          </p>
        </div>
      ) : null}

      {stimulus && !state.matches('completed') ? (
        <>
          <Stimulus stimulus={stimulus} disabled={state.matches('feedback') || isPlayingPitch} onAnswer={answer} onReplayPitch={replayPitch} />
          <div className={`feedback ${feedback ?? ''}`} aria-live="polite">
            {feedback === 'correct' ? 'yes — the edge moved inward' : feedback === 'wrong' ? 'no — the edge breathed outward' : 'choose the interval that breaks sameness'}
          </div>
        </>
      ) : null}

      {state.matches('completed') && workerState ? (
        <div className="complete-slate">
          <p className="eyebrow">edge found</p>
          <h2>{formatThreshold(selected, scaleForDisplay(selected, workerState.threshold))}</h2>
          <p>
            68% credible interval: {formatThreshold(selected, scaleForDisplay(selected, workerState.low))} → {formatThreshold(selected, scaleForDisplay(selected, workerState.high))}.
          </p>
          <button className="primary-button" onClick={start}>Run again</button>
        </div>
      ) : null}
    </section>
  );
}
